import { useEffect, useRef, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWaMsg, waUrl, vehicleShort } from "@/lib/waTemplates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wrench, Star, MessageCircle, Copy } from "lucide-react";

export const laborPrice = (s, rate) => (s.price != null ? Number(s.price) : s.hours && rate ? Math.round(Number(s.hours) * Number(rate) * 100) / 100 : 0);

export function LaborPicker({ onPick, inline = false, autoFocus = false }) {
  const { user } = useAuth();
  const rate = user?.shop?.hourly_rate;
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const t = useRef(null);
  useEffect(() => {
    clearTimeout(t.current);
    if (!open) { return undefined; }
    t.current = setTimeout(() => { api.get("/catalog/search", { params: { q } }).then(({ data }) => setList(data)).catch(() => setList([])); }, 250);
    return () => clearTimeout(t.current);
  }, [q, open]);
  return (
    <div className={inline ? "mt-2 rounded-xl border border-zinc-800 bg-zinc-950 p-2" : "relative mt-2"} data-testid="labor-picker">
      <div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-zinc-500" />
        <Input data-testid="labor-search" autoFocus={autoFocus} value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Buscar peça ou serviço…" className="bg-zinc-900 border-zinc-800 text-base w-full" /></div>
      {open && q.trim() && (
        <div className={inline ? "mt-2 max-h-64 overflow-y-auto rounded-lg border border-zinc-800" : "absolute z-30 left-6 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl"}>
          {list.length === 0 ? <div className="p-3 text-xs text-zinc-500">Nada encontrado. Cadastre em Estoque ou Mão de obra.</div> : list.map((s) => (
            <button key={`${s.type}-${s.id}`} type="button" data-testid={`catalog-opt-${s.id}`} onClick={() => { onPick(s.type === "peca" ? { description: s.name, quantity: 1, unit_price: s.price, product_id: s.id, code: s.code || null } : { description: s.name, hours: s.hours ?? null, quantity: 1, unit_price: s.price, code: s.code || null, cost: s.cost ?? null }); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-zinc-900 flex justify-between gap-2 text-sm">
              <span className="text-zinc-100 min-w-0"><span className={`mr-2 rounded px-1.5 text-[10px] font-bold uppercase ${s.type === "peca" ? "bg-sky-900/60 text-sky-300" : "bg-amber-900/60 text-amber-300"}`}>{s.type === "peca" ? "Peça" : "Serviço"}</span>{s.code ? <span className="text-zinc-500 text-xs mr-1">{s.code}</span> : null}{s.name}</span>
              <span className="text-zinc-400 whitespace-nowrap">{s.type === "servico" && s.hours ? `${s.hours}h · ` : ""}{brl(s.price)}</span>
            </button>
          ))}
          <button type="button" onClick={() => setOpen(false)} className="w-full text-xs text-zinc-500 py-2 border-t border-zinc-800">Fechar</button>
        </div>
      )}
    </div>
  );
}

export function SurveyButton({ order }) {
  const [d, setD] = useState(null);
  const waMsg = useWaMsg();
  useEffect(() => { api.post(`/orders/${order.id}/survey`).then(({ data }) => setD(data)).catch(() => setD(null)); }, [order.id]);
  if (!d) return null;
  const text = waMsg("survey", { cliente: order.customer_name || "", placa: order.vehicle?.plate || "", modelo: vehicleShort(order.vehicle), link: d.url });
  return (
    <div data-testid="survey-card" className="flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1 text-xs font-mono-caps text-zinc-500 uppercase"><Star className="h-4 w-4" /> Pesquisa de satisfação</span>
      {d.rating ? <span data-testid="survey-rating" className="text-sm text-amber-300 font-bold">{"★".repeat(d.rating)}{"☆".repeat(5 - d.rating)} {d.comment ? `— “${d.comment}”` : ""}</span> : <>
        {order.customer_phone && <a data-testid="survey-wa" href={waUrl(order.customer_phone, text)} target="_blank" rel="noopener noreferrer" className="h-8 px-3 inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs"><MessageCircle className="h-3.5 w-3.5" /> Enviar no WhatsApp</a>}
        <Button data-testid="survey-copy" size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(d.url).then(() => toast.success("Link copiado"), () => toast.info(d.url))} className="h-8 bg-zinc-900 border-zinc-800"><Copy className="h-3.5 w-3.5 mr-1" /> Copiar link</Button>
        <span className="text-[11px] text-zinc-500">E-mail enviado automaticamente ao marcar Entregue (se o cliente tiver e-mail).</span>
      </>}
    </div>
  );
}

export function LaborRateField() {
  const { user, refresh } = useAuth();
  const [v, setV] = useState(user?.shop?.hourly_rate ?? "");
  const save = async () => {
    try { await api.put("/shop-settings/labor-rate", { hourly_rate: v === "" ? null : Number(v) }); toast.success("Valor por hora salvo"); refresh?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex items-end gap-2" data-testid="labor-rate">
      <label className="text-[11px] text-zinc-400">Valor da hora (R$)<Input data-testid="labor-rate-input" type="number" value={v} onChange={(e) => setV(e.target.value)} className="mt-1 w-32 bg-zinc-900 border-zinc-800" /></label>
      <Button data-testid="labor-rate-save" size="sm" variant="outline" onClick={save} className="bg-zinc-900 border-zinc-800">Salvar</Button>
    </div>
  );
}
